<template>
  <div class="security-page">
    <section class="admin-page-heading security-hero mb-6">
      <div>
        <div class="text-overline primary--text font-weight-bold">
          Organization security
        </div>
        <h1 class="text-h4 font-weight-bold mb-2">Terminal verification</h1>
        <p class="text-body-1 admin-muted mb-0">
          Protect employee access at every terminal and location switch.
        </p>
      </div>
      <v-chip
        v-if="ownerAccessRequired"
        color="info"
        text-color="white"
        label
      >
        <v-icon left small>mdi-account-lock-outline</v-icon>
        Owner access only
      </v-chip>
      <v-chip
        v-else
        :color="enabled ? 'success' : 'grey'"
        :text-color="enabled ? 'white' : undefined"
        label
      >
        <v-icon left small>{{
          enabled ? "mdi-shield-check" : "mdi-shield-outline"
        }}</v-icon>
        {{ enabled ? "Protection on" : "Protection off" }}
      </v-chip>
    </section>
    <v-card
      v-if="ownerAccessRequired"
      outlined
      class="admin-panel rounded-xl mb-6"
    >
      <v-card-text class="pa-5 pa-sm-6">
        <div class="security-access-message">
          <v-avatar color="info" size="48" class="security-icon">
            <v-icon dark>mdi-account-lock-outline</v-icon>
          </v-avatar>
          <div>
            <h2 class="text-h6 font-weight-bold mb-1">
              Organization owner access required
            </h2>
            <p class="text-body-2 admin-muted mb-0">
              Only the organization owner can view or change terminal
              verification settings. Contact your organization owner if you
              need this feature enabled or updated.
            </p>
          </div>
        </div>
      </v-card-text>
    </v-card>
    <v-card
      v-else
      outlined
      class="admin-panel rounded-xl mb-6"
      :loading="loading"
    >
      <v-card-text class="pa-5 pa-sm-6">
        <v-alert v-if="error" outlined type="error">{{ error }}</v-alert>
        <div class="security-policy">
          <v-avatar color="primary" size="48" class="security-icon"
            ><v-icon dark>mdi-monitor-lock</v-icon></v-avatar
          >
          <div class="security-policy__copy">
            <h2 class="text-h6 font-weight-bold mb-1">Require verification</h2>
            <p class="text-body-2 admin-muted mb-0">
              Ask for an authenticator, text-message, or recovery code before
              showing employees.
            </p>
          </div>
          <v-switch
            v-model="enabled"
            inset
            color="primary"
            :aria-label="
              enabled ? 'Disable terminal MFA' : 'Enable terminal MFA'
            "
            :disabled="saving || !hasVerifiedFactor"
            @change="savePolicy"
          />
        </div>
        <v-alert
          v-if="!hasVerifiedFactor"
          class="mt-4 mb-0"
          dense
          outlined
          type="info"
          >Add and verify a method before turning on terminal
          verification.</v-alert
        >
      </v-card-text>
    </v-card>
    <v-card
      v-if="!ownerAccessRequired"
      outlined
      class="admin-panel rounded-xl"
    >
      <v-card-title class="admin-panel__header pa-5 pa-sm-6">
        <div>
          <h2 class="text-h6 font-weight-bold mb-1">Verification methods</h2>
          <p class="text-body-2 admin-muted mb-0">
            Choose how administrators verify terminal access.
          </p>
        </div>
        <div class="security-actions">
          <v-btn color="primary" depressed rounded @click="openTotpGuidance"
            ><v-icon left>mdi-shield-key-outline</v-icon>Add
            authenticator</v-btn
          ><v-btn outlined color="primary" rounded @click="openSms"
            ><v-icon left>mdi-cellphone-message</v-icon>Add text message</v-btn
          >
        </div>
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-5 pa-sm-6">
        <v-row v-if="factors.length">
          <v-col v-for="factor in factors" :key="factor.id" cols="12" md="6">
            <v-card
              outlined
              class="security-factor rounded-xl pa-4"
              height="100%"
              ><div class="d-flex align-center">
                <v-avatar color="primary" size="42"
                  ><v-icon dark>{{
                    factor.method === "sms"
                      ? "mdi-cellphone-message"
                      : "mdi-shield-key-outline"
                  }}</v-icon></v-avatar
                >
                <div class="ml-3 min-width-0">
                  <div class="font-weight-bold">
                    {{
                      factor.method === "sms"
                        ? "Text message"
                        : "Authenticator app"
                    }}
                  </div>
                  <div class="text-body-2 admin-muted text-truncate">
                    {{
                      factor.maskedDestination ||
                      (factor.confirmedAt
                        ? "Verified and ready"
                        : "Not verified")
                    }}
                  </div>
                </div>
                <v-chip
                  x-small
                  color="success"
                  text-color="white"
                  class="ml-auto"
                  >Verified</v-chip
                >
              </div>
              <v-card-actions class="px-0 pb-0 mt-3"
                ><v-spacer /><v-btn
                  text
                  color="error"
                  :disabled="saving"
                  @click="removeFactor(factor)"
                  >Remove</v-btn
                ></v-card-actions
              ></v-card
            >
          </v-col>
        </v-row>
        <div v-else class="security-empty text-center py-10">
          <v-avatar color="primary" size="60"
            ><v-icon dark size="32">mdi-shield-plus-outline</v-icon></v-avatar
          >
          <h3 class="text-h6 mt-4 mb-2">No verification methods yet</h3>
          <p class="text-body-2 admin-muted mb-0">
            Add an authenticator app or verified mobile number to get started.
          </p>
        </div>
        <v-divider class="my-5" />
        <div class="d-flex align-center flex-wrap">
          <div>
            <div class="font-weight-bold">Recovery codes</div>
            <div class="text-body-2 admin-muted">
              Replace the emergency codes for a verified method.
            </div>
          </div>
          <v-spacer /><v-btn
            text
            color="primary"
            :loading="recoveryLoading"
            :disabled="!hasVerifiedFactor || recoveryLoading"
            @click="regenerateRecoveryCodes"
            >Generate new codes</v-btn
          >
        </div>
      </v-card-text>
    </v-card>
    <v-dialog
      v-model="enrollDialog"
      v-drag-dialog
      content-class="draggable-dialog"
      persistent
      scrollable
      max-width="520"
      aria-labelledby="authenticator-dialog-title"
      ><v-card class="mfa-dialog-card"
        ><CardToolbarDialog
          title="Set up authenticator app"
          title-id="authenticator-dialog-title"
          icon="mdi-shield-key-outline"
          :disabled="saving"
          @cancel="closeEnroll"
        /><v-card-text class="mfa-dialog-body pt-5"
          ><v-alert
            v-if="dialogError"
            outlined
            dense
            type="error"
            role="alert"
            >{{ dialogError }}</v-alert
          >
          <div v-if="totpStep === 'intro'" class="authenticator-intro">
            <v-avatar color="primary" size="56" class="mb-4">
              <v-icon dark size="30">mdi-cellphone-key</v-icon>
            </v-avatar>
            <h3 class="text-h6 font-weight-bold mb-2">
              You’ll need an authenticator app
            </h3>
            <p class="text-body-2 admin-muted mb-5">
              An authenticator app creates a secure 6-digit code on your phone.
              If you already use one, continue to the next step. Otherwise,
              install Google Authenticator from your phone’s official app
              store, then return here.
            </p>
            <div class="authenticator-downloads">
              <v-card
                v-for="download in authenticatorDownloads"
                :key="download.platform"
                outlined
                class="authenticator-download-card rounded-xl pa-4 text-center"
              >
                <div class="font-weight-bold mb-1">
                  <v-icon color="primary" left>{{ download.icon }}</v-icon>
                  {{ download.platform }}
                </div>
                <p class="text-caption admin-muted mb-3">
                  Scan to download Google Authenticator.
                </p>
                <div class="authenticator-download-qr rounded-lg pa-2 mx-auto mb-3">
                  <v-img
                    v-if="download.qrCodeDataUrl"
                    :src="download.qrCodeDataUrl"
                    contain
                    width="132"
                    height="132"
                    :alt="`QR code to download Google Authenticator for ${download.platform}`"
                  />
                  <div
                    v-else
                    class="authenticator-download-qr__status"
                    role="status"
                    aria-live="polite"
                  >
                    <v-progress-circular
                      v-if="downloadQrsLoading"
                      indeterminate
                      color="primary"
                      size="32"
                    />
                    <v-icon v-else color="warning">mdi-qrcode-remove</v-icon>
                    <span class="text-caption mt-2">
                      {{
                        downloadQrsLoading
                          ? "Preparing download QR code"
                          : "QR unavailable—use the button below"
                      }}
                    </span>
                  </div>
                </div>
                <v-btn
                  outlined
                  color="primary"
                  block
                  :href="download.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  :aria-label="`Download Google Authenticator for ${download.platform} (opens in a new tab)`"
                >
                  <v-icon left>{{ download.icon }}</v-icon>
                  Download for {{ download.platform }}
                </v-btn>
              </v-card>
            </div>
            <v-alert type="info" outlined dense class="mt-5 mb-0">
              The QR codes above only download Google Authenticator. Your
              account setup QR code appears after you continue; scan that next
              QR inside the authenticator app.
            </v-alert>
          </div>
          <div v-else aria-live="polite">
            <h3 class="text-h6 font-weight-bold mb-2">
              Scan your setup code
            </h3>
            <p class="text-body-2 admin-muted">
              Open your authenticator app, add a new account, and scan this
              code. Then enter the 6-digit code shown in the app.
            </p>
            <div class="security-qr rounded-xl pa-4 mb-4">
              <v-img
                v-if="totp.qrCodeDataUrl"
                :src="totp.qrCodeDataUrl"
                contain
                max-height="220"
                alt="Authenticator setup QR code"
              />
            </div>
            <v-btn
              text
              small
              color="primary"
              :aria-expanded="String(manualSetupOpen)"
              aria-controls="manual-authenticator-setup"
              @click="manualSetupOpen = !manualSetupOpen"
              >{{
                manualSetupOpen
                  ? "Hide manual setup"
                  : "Can’t scan? Use manual setup"
              }}</v-btn
            >
            <v-expand-transition
              ><div
                v-if="manualSetupOpen"
                id="manual-authenticator-setup"
                class="manual-setup rounded-lg pa-3 mb-4"
              >
                <div class="text-caption admin-muted mb-2">
                  Copy this URI into your authenticator app.
                </div>
                <div class="manual-setup__value text-body-2">
                  {{ totp.otpauthUri }}
                </div>
                <v-btn
                  x-small
                  text
                  color="primary"
                  class="mt-2"
                  @click="copySetupUri"
                  >Copy setup URI</v-btn
                ><span class="text-caption ml-2" aria-live="polite">{{
                  setupCopyMessage
                }}</span>
              </div></v-expand-transition
            >
            <v-text-field
              v-model.trim="confirmCode"
              outlined
              dense
              label="6-digit code"
              inputmode="numeric"
              maxlength="6"
              autocomplete="one-time-code" />
          </div></v-card-text
        ><v-divider /><v-card-actions
          ><v-spacer /><v-btn text @click="closeEnroll">Cancel</v-btn
          ><v-btn
            color="primary"
            :loading="saving"
            :disabled="totpStep === 'setup' && !/^[0-9]{6}$/.test(confirmCode)"
            @click="totpStep === 'intro' ? continueTotp() : confirmTotp()"
            >{{ totpStep === "intro" ? "Continue to setup" : "Verify" }}</v-btn
          ></v-card-actions
        ></v-card
      ></v-dialog
    >
    <v-dialog
      v-model="smsDialog"
      v-drag-dialog
      content-class="draggable-dialog"
      persistent
      scrollable
      max-width="480"
      aria-labelledby="sms-dialog-title"
      ><v-card class="mfa-dialog-card"
        ><CardToolbarDialog
          title="Add text message"
          title-id="sms-dialog-title"
          icon="mdi-cellphone-message"
          :disabled="saving"
          @cancel="closeSms"
        /><v-card-text class="mfa-dialog-body pa-5"
          ><v-alert
            v-if="dialogError"
            role="alert"
            outlined
            dense
            type="error"
            >{{ dialogError }}</v-alert
          >
          <p v-if="!sms.factorId" class="text-body-2 admin-muted mb-4">
            Enter a U.S. mobile number that can receive verification texts.
            Text-message verification is available only for U.S. numbers. If
            you use an international number, choose an authenticator app
            instead.
          </p>
          <v-alert v-else type="info" outlined dense class="mb-4">
            A 6-digit code was sent to
            <strong>{{ sms.maskedDestination || "the verified number" }}</strong
            >. It expires shortly. If it expires, close this window and send a
            new code.
          </v-alert>
          <v-text-field
            v-if="!sms.factorId"
            v-model.trim="smsPhone"
            outlined
            dense
            type="tel"
            inputmode="tel"
            label="Mobile phone number"
            placeholder="(212) 555-0123"
            prepend-inner-icon="mdi-phone-outline"
            hint="Enter a 10-digit U.S. mobile number. We’ll add +1 automatically."
            persistent-hint
            autocomplete="tel"
            maxlength="18"
            :error-messages="smsPhoneError"
            @input="formatSmsPhone"
          :disabled="!!sms.factorId" /><v-text-field
            v-if="sms.factorId"
            v-model.trim="confirmCode"
            class="sms-code-field"
            outlined
            dense
            label="6-digit code"
            aria-label="6-digit text-message verification code"
            inputmode="numeric"
            maxlength="6"
            autocomplete="one-time-code" /></v-card-text
        ><v-divider /><v-card-actions
          ><v-spacer /><v-btn text @click="closeSms">Cancel</v-btn
          ><v-btn
            color="primary"
            :loading="saving"
            :disabled="sms.factorId ? !smsCodeValid : !smsPhoneValid"
            @click="sms.factorId ? confirmSms() : startSms()"
            >{{ sms.factorId ? "Verify" : "Send code" }}</v-btn
          ></v-card-actions
        ></v-card
      ></v-dialog
    >
    <v-dialog
      v-model="codesDialog"
      v-drag-dialog
      content-class="draggable-dialog"
      persistent
      scrollable
      max-width="520"
      aria-labelledby="recovery-dialog-title"
      ><v-card class="mfa-dialog-card"
        ><CardToolbarDialog
          title="Save recovery codes"
          title-id="recovery-dialog-title"
          icon="mdi-key-chain-variant"
          :disabled="recoveryEmailLoading"
          @cancel="closeCodes"
        /><v-card-text class="mfa-dialog-body pt-5"
          ><v-alert type="warning" outlined
            >These codes are shown once. Store them securely; each code can be
            used only once.</v-alert
          >
          <pre class="recovery-codes">{{ recoveryCodes.join("\n") }}</pre>
          <div class="d-flex align-center flex-wrap mb-5">
            <v-btn outlined color="primary" @click="copyRecoveryCodes"
              ><v-icon left>mdi-content-copy</v-icon>Copy codes</v-btn
            ><span class="text-caption ml-3" aria-live="polite">{{
              copyMessage
            }}</span>
          </div>
          <v-divider class="mb-5" />
          <h3 class="text-subtitle-1 font-weight-bold mb-1">
            Email a copy to yourself
          </h3>
          <p class="text-body-2 admin-muted mb-3">
            We can send these one-time recovery codes to an email address so
            you have a backup. Use a private, secure email account that only
            you can access. Anyone who can open that inbox can use these codes
            to pass terminal verification.
          </p>
          <v-alert v-if="recoveryEmailError" type="error" outlined dense>
            {{ recoveryEmailError }}
          </v-alert>
          <v-alert
            v-if="recoveryEmailSuccess"
            type="success"
            outlined
            dense
            role="status"
            aria-live="polite"
          >
            Recovery codes were emailed to {{ recoveryEmail }}.
          </v-alert>
          <div class="recovery-email-row">
            <v-text-field
              v-model.trim="recoveryEmail"
              outlined
              dense
              hide-details="auto"
              label="Email address"
              type="email"
              autocomplete="email"
              prepend-inner-icon="mdi-email-outline"
              aria-label="Email address for recovery codes"
              :error-messages="recoveryEmail && !recoveryEmailValid ? ['Enter a valid email address.'] : []"
              :disabled="recoveryEmailLoading || recoveryEmailSuccess"
              @input="clearRecoveryEmailStatus"
            />
            <v-btn
              color="primary"
              outlined
              height="40"
              :loading="recoveryEmailLoading"
              :disabled="!canEmailRecoveryCodes"
              @click="emailRecoveryCodes"
              aria-label="Email my recovery codes"
            >
              <v-icon left>mdi-email-send-outline</v-icon>Email my codes
            </v-btn>
          </div></v-card-text
        ><v-divider /><v-card-actions
          ><v-spacer /><v-btn
            color="primary"
            :disabled="recoveryEmailLoading"
            @click="closeCodes"
            >I saved these codes</v-btn
          ></v-card-actions
        ></v-card
      ></v-dialog
    >
  </div>
</template>
<script>
import settingsService from "../../service";
import QRCode from "qrcode";
import { mapGetters } from "vuex";
export default {
  name: "settings-security",
  data: () => ({
    loading: true,
    saving: false,
    error: "",
    ownerAccessRequired: false,
    dialogError: "",
    copyMessage: "",
    enabled: false,
    factors: [],
    enrollDialog: false,
    smsDialog: false,
    codesDialog: false,
    totp: {},
    sms: {},
    smsPhone: "",
    confirmCode: "",
    recoveryCodes: [],
    recoveryFactorId: null,
    recoveryLoading: false,
    recoveryEmail: "",
    recoveryEmailLoading: false,
    recoveryEmailSuccess: false,
    recoveryEmailError: "",
    manualSetupOpen: false,
    setupCopyMessage: "",
    totpStep: "intro",
    downloadQrsLoading: false,
    authenticatorDownloads: [
      {
        platform: "iPhone",
        icon: "mdi-apple",
        url: "https://apps.apple.com/app/google-authenticator/id388497605",
        qrCodeDataUrl: "",
      },
      {
        platform: "Android",
        icon: "mdi-google-play",
        url: "https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2",
        qrCodeDataUrl: "",
      },
    ],
  }),
  computed: {
    ...mapGetters("brand", ["currentBrand"]),
    ...mapGetters("global", ["currentUser"]),
    mfaBrand() {
      const supportedBrands = ["storesync", "autorepair", "repairnest"];
      return supportedBrands.includes(this.currentBrand)
        ? this.currentBrand
        : undefined;
    },
    hasVerifiedFactor() {
      return this.factors.some((f) => f.active && f.confirmedAt);
    },
    smsPhoneValid() {
      return this.smsPhoneDigits.length === 10;
    },
    smsPhoneDigits() {
      const digits = String(this.smsPhone || "").replace(/\D/g, "");
      return digits.length === 11 && digits.charAt(0) === "1"
        ? digits.slice(1)
        : digits;
    },
    smsPhoneError() {
      return this.smsPhone && !this.smsPhoneValid
        ? ["Enter a valid 10-digit U.S. mobile number."]
        : [];
    },
    smsCodeValid() {
      return /^[0-9]{6}$/.test(this.confirmCode);
    },
    recoveryEmailValid() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.recoveryEmail);
    },
    canEmailRecoveryCodes() {
      return (
        this.recoveryEmailValid &&
        !!this.recoveryFactorId &&
        this.recoveryCodes.length > 0 &&
        !this.recoveryEmailLoading &&
        !this.recoveryEmailSuccess
      );
    },
  },
  created() {
    this.load();
  },
  methods: {
    unwrap(r) {
      return r.data || r;
    },
    async load() {
      this.loading = true;
      this.error = "";
      this.ownerAccessRequired = false;
      try {
        const d = this.unwrap(await settingsService.Mfa.getStatus());
        this.enabled = !!d.enabled;
        this.factors = d.factors || [];
      } catch (e) {
        const response =
          (e && e.response && e.response.data) || (e && e.data) || {};
        if (response.code === "MFA_ADMIN_REQUIRED") {
          this.ownerAccessRequired = true;
        } else {
          this.error = "Security settings could not be loaded. Please try again.";
        }
      } finally {
        this.loading = false;
      }
    },
    async savePolicy() {
      this.saving = true;
      try {
        await settingsService.Mfa.updatePolicy({ enabled: this.enabled });
      } catch (e) {
        this.enabled = !this.enabled;
        this.error =
          (e.data && e.data.message) || "Security policy could not be updated.";
      } finally {
        this.saving = false;
      }
    },
    async openTotpGuidance() {
      this.error = "";
      this.dialogError = "";
      this.totp = {};
      this.confirmCode = "";
      this.manualSetupOpen = false;
      this.setupCopyMessage = "";
      this.totpStep = "intro";
      this.enrollDialog = true;
      await this.generateDownloadQrs();
    },
    async generateDownloadQrs() {
      if (this.authenticatorDownloads.every((download) => download.qrCodeDataUrl)) {
        return;
      }
      this.downloadQrsLoading = true;
      try {
        const qrCodes = await Promise.all(
          this.authenticatorDownloads.map((download) =>
            QRCode.toDataURL(download.url, { width: 180, margin: 1 })
          )
        );
        this.authenticatorDownloads = this.authenticatorDownloads.map(
          (download, index) => ({
            ...download,
            qrCodeDataUrl: qrCodes[index],
          })
        );
      } catch (e) {
        this.authenticatorDownloads = this.authenticatorDownloads.map(
          (download) => ({ ...download, qrCodeDataUrl: "" })
        );
      } finally {
        this.downloadQrsLoading = false;
      }
    },
    async continueTotp() {
      this.saving = true;
      this.dialogError = "";
      try {
        this.totp = this.unwrap(
          await settingsService.Mfa.enrollTotp({ brand: this.mfaBrand })
        );
        this.totp.qrCodeDataUrl = await QRCode.toDataURL(this.totp.otpauthUri, {
          width: 240,
          margin: 1,
        });
        this.confirmCode = "";
        this.manualSetupOpen = false;
        this.setupCopyMessage = "";
        this.totpStep = "setup";
      } catch (e) {
        this.dialogError =
          (e.data && e.data.message) ||
          "Authenticator setup could not be started. Please try again.";
      } finally {
        this.saving = false;
      }
    },
    async confirmTotp() {
      this.saving = true;
      this.dialogError = "";
      try {
        const d = this.unwrap(
          await settingsService.Mfa.confirmTotp({
            factorId: this.totp.factorId,
            code: this.confirmCode,
          })
        );
        this.enrollDialog = false;
        this.applyConfirmationState(d);
        this.showCodes(
          d.recoveryCodes,
          (d.factor && d.factor.id) || this.totp.factorId
        );
        await this.load();
      } catch (e) {
        this.dialogError =
          (e.data && e.data.message) || "The code could not be verified.";
      } finally {
        this.saving = false;
      }
    },
    closeEnroll() {
      if (!this.saving) {
        this.enrollDialog = false;
        this.totp = {};
        this.confirmCode = "";
        this.dialogError = "";
        this.totpStep = "intro";
      }
    },
    closeSms() {
      if (!this.saving) {
        this.smsDialog = false;
        this.sms = {};
        this.confirmCode = "";
        this.dialogError = "";
      }
    },
    openSms() {
      this.sms = {};
      this.smsPhone = "";
      this.confirmCode = "";
      this.dialogError = "";
      this.smsDialog = true;
    },
    formatSmsPhone(value) {
      let digits = String(value || "").replace(/\D/g, "");
      if (digits.length === 11 && digits.charAt(0) === "1") {
        digits = digits.slice(1);
      }
      digits = digits.slice(0, 10);

      if (digits.length <= 3) {
        this.smsPhone = digits;
      } else if (digits.length <= 6) {
        this.smsPhone = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      } else {
        this.smsPhone = `(${digits.slice(0, 3)}) ${digits.slice(
          3,
          6
        )}-${digits.slice(6)}`;
      }
    },
    async copySetupUri() {
      try {
        await navigator.clipboard.writeText(this.totp.otpauthUri);
        this.setupCopyMessage = "Copied";
      } catch (e) {
        this.setupCopyMessage = "Copy failed";
      }
    },
    async startSms() {
      this.saving = true;
      this.dialogError = "";
      try {
        this.sms = this.unwrap(
          await settingsService.Mfa.enrollSms({
            phoneNumber: `+1${this.smsPhoneDigits}`,
          })
        );
        this.confirmCode = "";
      } catch (e) {
        this.dialogError =
          (e.data && e.data.message) || "The text message could not be sent.";
      } finally {
        this.saving = false;
      }
    },
    async confirmSms() {
      this.saving = true;
      this.dialogError = "";
      try {
        const factorId = this.sms.factorId;
        const d = this.unwrap(
          await settingsService.Mfa.confirmSms({
            factorId,
            challengeId: this.sms.challengeId,
            code: this.confirmCode,
          })
        );
        this.smsDialog = false;
        this.sms = {};
        this.applyConfirmationState(d);
        this.showCodes(
          d.recoveryCodes,
          (d.factor && d.factor.id) || factorId
        );
        await this.load();
      } catch (e) {
        this.dialogError =
          (e.data && e.data.message) || "The code could not be verified.";
      } finally {
        this.saving = false;
      }
    },
    async removeFactor(f) {
      const result = await this.$swal({
        title: "Remove verification method?",
        text: "Terminal access may be affected if no other method remains.",
        icon: "warning",
        showCancelButton: true,
      });
      if (!result.value) return;
      this.saving = true;
      try {
        await settingsService.Mfa.removeFactor(f.id);
        await this.load();
      } catch (e) {
        this.error =
          (e.data && e.data.message) ||
          "The verification method could not be removed.";
      } finally {
        this.saving = false;
      }
    },
    async regenerateRecoveryCodes() {
      const factor = this.factors.find((f) => f.active && f.confirmedAt);
      if (!factor) return;
      const result = await this.$swal({
        title: "Replace recovery codes?",
        text: "Existing recovery codes for this method will stop working.",
        icon: "warning",
        showCancelButton: true,
      });
      if (!result.value) return;
      this.recoveryLoading = true;
      this.error = "";
      try {
        const d = this.unwrap(
          await settingsService.Mfa.regenerateRecoveryCodes(factor.id)
        );
        this.showCodes(d.recoveryCodes, d.factorId || factor.id);
      } catch (e) {
        this.error =
          (e.data && e.data.message) ||
          "Recovery codes could not be generated. Please try again.";
      } finally {
        this.recoveryLoading = false;
      }
    },
    applyConfirmationState(data) {
      if (data && typeof data.enabled === "boolean") {
        this.enabled = data.enabled;
      } else if (data && data.policy && typeof data.policy.enabled === "boolean") {
        this.enabled = data.policy.enabled;
      }
      if (data && data.factor) {
        this.factors = [
          ...this.factors.filter((factor) => factor.id !== data.factor.id),
          data.factor,
        ];
      }
    },
    showCodes(codes, factorId) {
      if (Array.isArray(codes) && codes.length) {
        this.recoveryCodes = codes;
        this.recoveryFactorId = factorId || null;
        this.recoveryEmail = (this.currentUser && this.currentUser.email) || "";
        this.recoveryEmailLoading = false;
        this.recoveryEmailSuccess = false;
        this.recoveryEmailError = "";
        this.copyMessage = "";
        this.codesDialog = true;
      }
    },
    clearRecoveryEmailStatus() {
      this.recoveryEmailError = "";
      this.recoveryEmailSuccess = false;
    },
    async emailRecoveryCodes() {
      if (!this.canEmailRecoveryCodes) return;
      this.recoveryEmailLoading = true;
      this.recoveryEmailError = "";
      try {
        await settingsService.Mfa.emailRecoveryCodes(this.recoveryFactorId, {
          email: this.recoveryEmail,
          recoveryCodes: this.recoveryCodes,
          brand: this.mfaBrand,
        });
        this.recoveryEmailSuccess = true;
      } catch (e) {
        const response =
          (e && e.response && e.response.data) || (e && e.data) || {};
        this.recoveryEmailError =
          response.message ||
          "Recovery codes could not be emailed. Copy and save them before closing this window.";
      } finally {
        this.recoveryEmailLoading = false;
      }
    },
    async copyRecoveryCodes() {
      try {
        await navigator.clipboard.writeText(this.recoveryCodes.join("\n"));
        this.copyMessage = "Copied";
      } catch (e) {
        this.copyMessage = "Copy failed. Select the codes manually.";
      }
    },
    closeCodes() {
      if (this.recoveryEmailLoading) return;
      this.recoveryCodes = [];
      this.recoveryFactorId = null;
      this.recoveryEmail = "";
      this.recoveryEmailLoading = false;
      this.recoveryEmailSuccess = false;
      this.recoveryEmailError = "";
      this.codesDialog = false;
      this.copyMessage = "";
    },
  },
};
</script>
<style scoped>
.security-page {
  max-width: 1180px;
  margin: 0 auto;
}
.security-page .admin-muted {
  color: currentColor;
  opacity: 0.66;
}
.security-hero {
  min-height: 72px;
}
.security-policy {
  display: flex;
  align-items: center;
  gap: 16px;
}
.security-access-message {
  display: flex;
  align-items: center;
  gap: 16px;
}
.security-policy__copy {
  flex: 1 1 auto;
  min-width: 0;
}
.security-icon,
.security-factor .v-avatar {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.14);
}
.security-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.security-factor {
  transition: border-color 160ms ease, box-shadow 160ms ease;
}
.security-factor:hover {
  border-color: var(--brand-primary);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}
.security-empty,
.security-qr,
.manual-setup,
.recovery-codes {
  background: rgba(127, 127, 127, 0.1);
}
.recovery-email-row {
  align-items: start;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1fr) auto;
}
.security-qr {
  margin-left: auto;
  margin-right: auto;
  max-width: 280px;
}
.manual-setup__value {
  overflow-wrap: anywhere;
  max-height: 72px;
  overflow-y: auto;
}
.authenticator-downloads {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.authenticator-downloads .v-btn {
  margin: 0;
}
.authenticator-download-card {
  min-width: 0;
}
.authenticator-download-qr {
  background: rgba(127, 127, 127, 0.1);
  height: 148px;
  width: 148px;
}
.authenticator-download-qr__status {
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 132px;
  justify-content: center;
}
.mfa-dialog-card {
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 20px;
}
.mfa-dialog-body {
  min-height: 0;
  overflow-y: auto;
}
.mfa-dialog-card .v-card__actions {
  flex: 0 0 auto;
  padding: 16px 20px;
}
::v-deep .sms-code-field input:-webkit-autofill,
::v-deep .sms-code-field input:-webkit-autofill:hover,
::v-deep .sms-code-field input:-webkit-autofill:focus {
  -webkit-text-fill-color: currentColor;
  caret-color: currentColor;
  transition: background-color 5000s ease-in-out 0s;
}
.recovery-codes {
  white-space: pre-wrap;
  font-size: 1rem;
  line-height: 1.8;
  border-radius: 12px;
  padding: 16px;
}
@media (max-width: 599px) {
  .security-policy {
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .security-access-message {
    align-items: flex-start;
  }
  .security-policy .v-input--switch {
    flex-basis: 100%;
    margin-left: 64px;
  }
  .security-actions,
  .security-actions .v-btn {
    width: 100%;
  }
  .mfa-dialog-card .v-card__actions {
    align-items: stretch;
    flex-direction: column-reverse;
  }
  .mfa-dialog-card .v-card__actions .v-btn {
    margin: 4px 0;
    width: 100%;
  }
  .authenticator-downloads {
    grid-template-columns: 1fr;
  }
  .recovery-email-row {
    grid-template-columns: 1fr;
  }
  .recovery-email-row .v-btn {
    margin: 0;
    width: 100%;
  }
}
</style>
